import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Divider,
  Box,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocalization } from '@/hooks/useLocalization';
import {
  SavedInvestorFilter,
  useGetUserSettingsQuery,
  useUpdateUserSettingMutation,
} from '@/store/settingsApi';
import { Report, useUpdateReportMutation } from '@/store/reportsApi';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import ChangeSavedFilterDialog from './ChangeSavedFilterDialog';
import { ensureSavedFiltersHaveIds } from '@/utils/savedFilters';

interface ReportFilters {
  ExcludedAccounts?: number[];
  ExcludedCategories?: number[];
  ExcludedAccountTypes?: number[];
  FilterId?: string;
  FilterText?: string;
  limit?: number | null;
}

const parseReportFilters = (filtersJson: string | undefined): ReportFilters => {
  if (!filtersJson) return {};
  try {
    const parsed = JSON.parse(filtersJson) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as ReportFilters;
    }
    return {};
  } catch {
    return {};
  }
};

interface ManageSavedFiltersDialogProps {
  open: boolean;
  onClose: () => void;
  savedFilters: SavedInvestorFilter[];
  onChangeFilters?: (filterId: string) => void;
  currentReport?: Report | null;
}

const ManageSavedFiltersDialog: React.FC<ManageSavedFiltersDialogProps> = ({
  open,
  onClose,
  savedFilters,
  currentReport,
}) => {
  const { t } = useLocalization();
  useGetUserSettingsQuery();
  const [updateUserSetting] = useUpdateUserSettingMutation();
  const [updateReport] = useUpdateReportMutation();

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingOldText, setEditingOldText] = React.useState<string>('');
  const [editingText, setEditingText] = React.useState<string>('');

  // Change Filters state
  const [changeDialogOpen, setChangeDialogOpen] = React.useState(false);
  const [changeTarget, setChangeTarget] =
    React.useState<SavedInvestorFilter | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<{
    id: string;
    text: string;
  } | null>(null);

  const currentFilters = React.useMemo(
    () => ensureSavedFiltersHaveIds(savedFilters),
    [savedFilters],
  );

  const sortedFilters = React.useMemo(
    () => [...currentFilters].sort((a, b) => a.text.localeCompare(b.text)),
    [currentFilters],
  );

  const persist = async (updated: SavedInvestorFilter[]) => {
    try {
      await updateUserSetting({
        Key: 'SavedInvestorFilters',
        Value: JSON.stringify(updated),
      }).unwrap();
    } catch (err) {
      console.error('Failed to update SavedInvestorFilters:', err);
    }
  };

  const handleStartRename = (filter: SavedInvestorFilter) => {
    setEditingId(filter.id!);
    setEditingText(filter.text);
    setEditingOldText(filter.text);
  };

  const handleConfirmRename = async () => {
    if (!editingId) return;
    const updated = currentFilters.map((f) =>
      f.id === editingId ? { ...f, text: editingText.trim() || f.text } : f,
    );
    await persist(updated);

    // If a report is currently selected and references this filter, update its Filters.FilterText
    if (
      typeof currentReport !== 'undefined' &&
      currentReport &&
      currentReport.ReportsUserReportId
    ) {
      try {
        const parsed = parseReportFilters((currentReport as Report).Filters);

        const referencesById =
          parsed.FilterId !== undefined && parsed.FilterId === editingId;
        const referencesByText =
          parsed.FilterId === undefined && parsed.FilterText === editingOldText;

        if (referencesById || referencesByText) {
          const newFilters: ReportFilters = {
            ...parsed,
            FilterId: editingId,
            FilterText: editingText.trim() || editingOldText,
          };

          await updateReport({
            ReportsUserReportId: (currentReport as Report).ReportsUserReportId!,
            Filters: JSON.stringify(newFilters),
          }).unwrap();
        }
      } catch {
        // best-effort; ignore
      }
    }

    setEditingId(null);
    setEditingText('');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditingText('');
  };

  const handleDelete = async (id: string) => {
    const updated = currentFilters.filter((f) => f.id !== id);
    await persist(updated);
  };

  const handleChangeFilters = (id: string) => {
    const target = currentFilters.find((f) => f.id === id) || null;
    if (!target) return;
    setChangeTarget(target);
    setChangeDialogOpen(true);
  };

  const requestDelete = (filter: SavedInvestorFilter) => {
    if (!filter.id) return;
    setDeleteTarget({ id: filter.id, text: filter.text });
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h6'>
            {t('manageSavedFilters', 'Manage Saved Filters')}
          </Typography>
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {sortedFilters.map((filter) => (
              <Box
                key={filter.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ flex: 1, pr: 2 }}>
                  {editingId === filter.id ? (
                    <TextField
                      size='small'
                      fullWidth
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                    />
                  ) : (
                    <Typography
                      variant='body1'
                      sx={{ fontWeight: 500 }}
                    >
                      {filter.text}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingId === filter.id ? (
                    <>
                      <Button
                        size='small'
                        variant='contained'
                        onClick={handleConfirmRename}
                      >
                        {t('save', 'Save')}
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        color='inherit'
                        onClick={handleCancelRename}
                      >
                        {t('cancel', 'Cancel')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => handleStartRename(filter)}
                      >
                        {t('rename', 'Rename')}
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={() => handleChangeFilters(filter.id!)}
                      >
                        {t('changeFilters', 'Change Filters')}
                      </Button>
                      <Button
                        size='small'
                        variant='outlined'
                        color='error'
                        onClick={() => requestDelete(filter)}
                      >
                        {t('delete', 'Delete')}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            onClick={onClose}
          >
            {t('close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Filters Dialog */}
      <ChangeSavedFilterDialog
        open={changeDialogOpen}
        filter={changeTarget}
        onClose={() => setChangeDialogOpen(false)}
        onSave={async ({
          excludedAccounts,
          excludedCategories,
          excludedAccountTypes,
        }) => {
          if (!changeTarget) return;
          const updated = currentFilters.map((f) =>
            f.id === changeTarget.id
              ? {
                  ...f,
                  filters: {
                    ...f.filters,
                    ExcludedAccounts:
                      excludedAccounts.length > 0
                        ? excludedAccounts
                        : undefined,
                    ExcludedCategories:
                      excludedCategories.length > 0
                        ? excludedCategories
                        : undefined,
                    ExcludedAccountTypes:
                      excludedAccountTypes.length > 0
                        ? excludedAccountTypes
                        : undefined,
                  },
                }
              : f,
          );
          const sortedUpdated = [...updated].sort((a, b) =>
            a.text.localeCompare(b.text),
          );
          await persist(sortedUpdated);
        }}
      />

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          if (deleteTarget?.id) {
            await handleDelete(deleteTarget.id);
          }
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        title={t('areYouSure', 'Are you sure?')}
        contentText={
          <Typography color='error.main'>
            {t(
              'deleteSavedFilterConfirm',
              'Are you sure you want to delete "{{name}}"?',
              { name: deleteTarget?.text || '' },
            )}
          </Typography>
        }
        confirmButtonText={t('delete', 'Delete')}
        cancelButtonText={t('cancel', 'Cancel')}
        confirmButtonColor='error'
      />
    </>
  );
};

export default ManageSavedFiltersDialog;
